import { TypeOf, z } from 'zod';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Form, TextInputField, TextAreaField } from '@repo/ui/Form';
import { useZodForm } from '@repo/hooks/useZodForm';
import { Button } from '@repo/ui/Button';

const createApiKeyForAccountFormSchema = z.object({
  name: z.string().min(2, { message: 'Api Key name must contain at least 2 characters.' }),
  notes: z.string().optional(),
});

type CreateApiKeyForAccountFormSchema = TypeOf<typeof createApiKeyForAccountFormSchema>;

const CREATE_API_KEY_FOR_ACCOUNT = gql`
  mutation CreateApiKeyForAccount($accountId: ID!, $input: CreateApiKeyForAccountInput!) {
    createApiKeyForAccount(accountId: $accountId, input: $input) {
      apiKey {
        id
        account {
          id
        }
      }
    }
  }
`;

interface CreateApiKeyForAccountProps {
  accountId: string;
  /** Called with the new key's id once it is created. */
  onCreated?: (apiKeyId: string) => void;
}

const CreateApiKeyForAccount = ({ accountId, onCreated }: CreateApiKeyForAccountProps) => {
  const client = useApolloClient();

  const onSubmit = async (input: CreateApiKeyForAccountFormSchema) => {
    const result = await client.mutate({
      mutation: CREATE_API_KEY_FOR_ACCOUNT,
      variables: { accountId, input },
    });
    const apiKeyId = (result.data as { createApiKeyForAccount?: { apiKey?: { id: string } } })
      ?.createApiKeyForAccount?.apiKey?.id;

    if (apiKeyId) onCreated?.(apiKeyId);
  };

  const form = useZodForm({
    schema: createApiKeyForAccountFormSchema,
    onSubmit,
  });

  return (
    <Form {...form} column={1}>
      <Form.Success>Api Key successfully created.</Form.Success>
      <Form.Error>
        Unable to create Api Key. Please try again later or contact support if error persists.
      </Form.Error>

      <TextInputField id="name" label="Name" {...form.register('name')} />
      <TextAreaField id="notes" label="Description" {...form.register('notes')} />
      <Button type="submit">Create Api Key</Button>
    </Form>
  );
};

export default CreateApiKeyForAccount;
